import { Button } from '../components/admin/ui';
import { Fragment, useEffect, useState } from 'react';
import { Loader2, ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, AuditLogEntry } from '../lib/admin-api';

export function AuditLogPage() {
  const { profile } = useAuth();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchEntries();
  }, [profile?.organization_id]);

  async function fetchEntries(offset = 0) {
    if (!profile?.organization_id) return;
    if (offset === 0) setLoading(true);

    const data = await adminApi.getAuditLog(profile.organization_id, {
      limit: 50,
      offset,
    });

    if (offset === 0) {
      setEntries(data);
    } else {
      setEntries((prev) => [...prev, ...data]);
    }

    setHasMore(data.length === 50);
    setLoading(false);
  }

  const getActionColor = (action: string) => {
    if (action.includes('create') || action.includes('add')) return 'text-success-foreground bg-success';
    if (action.includes('delete') || action.includes('remove')) return 'text-error-text bg-destructive/10';
    if (action.includes('update') || action.includes('edit')) return 'text-info-foreground bg-info';
    return 'text-muted-foreground bg-muted';
  };

  const getActorTypeColor = (type: string) => {
    switch (type) {
      case 'user': return 'bg-info text-info-foreground';
      case 'agent': return 'bg-primary/15 text-link';
      case 'ai_agent': return 'bg-info text-info-foreground';
      case 'system': return 'bg-muted text-foreground';
      case 'api': return 'bg-warning text-warning-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  return (
    <div className="support-page">
      <div>
        <h1 className="support-page-title">Audit log</h1>
        <p className="text-muted-foreground mt-1">
          Track all changes made to your organization
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-link" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No audit log entries yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Actions will appear here as they happen
          </p>
        </div>
      ) : (
        <>
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Actor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Resource</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <tr className="hover:bg-muted">
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getActorTypeColor(entry.actor_type)}`}>
                            {entry.actor_type}
                          </span>
                          <span className="text-foreground">
                            {entry.actor?.display_name || entry.actor?.email || entry.actor_id?.slice(0, 8) || 'System'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${getActionColor(entry.action)}`}>
                          {entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {entry.resource_type}
                        {entry.resource_id && (
                          <span className="text-muted-foreground ml-1 font-mono text-xs">
                            #{entry.resource_id.slice(0, 8)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(entry.old_values || entry.new_values) && (
                          <Button aria-label="Toggle details" size="iconSm" variant="ghost"
                            onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                            className="text-muted-foreground hover:text-muted-foreground"
                          >
                            {expandedId === entry.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 bg-muted">
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            {entry.old_values && (
                              <div>
                                <h4 className="font-semibold text-muted-foreground mb-1">Old Values</h4>
                                <pre className="bg-card p-2 rounded border border-border overflow-auto max-h-40 text-foreground">
                                  {JSON.stringify(entry.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {entry.new_values && (
                              <div>
                                <h4 className="font-semibold text-muted-foreground mb-1">New Values</h4>
                                <pre className="bg-card p-2 rounded border border-border overflow-auto max-h-40 text-foreground">
                                  {JSON.stringify(entry.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="text-center">
              <Button variant="ghost"
                onClick={() => fetchEntries(entries.length)}
                className="px-4 py-2 text-sm text-link hover:text-link font-medium"
              >
                Load more entries
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
